import {
  APARTMENT_COLUMNS,
  APARTMENT_ERROR_MESSAGES,
  APARTMENT_SOURCE_COLUMNS,
  type ApartmentRowStatus,
  CELL_ERROR_MESSAGES,
  type UnitBatchRowStatus,
  type UnitCellField,
  extraSlotColumns,
  parseSlotKey,
} from '@sivan/shared';
import type { RowState } from './pending';

export const FIELD_LABELS: Readonly<Record<UnitCellField, string>> = {
  code: 'קוד',
  displayNumber: 'מספר תצוגה',
  kind: 'סוג',
  building: 'בניין',
  floor: 'קומה',
  modelType: 'טיפוס',
  rooms: 'חדרים',
  areaM2: 'שטח (מ״ר)',
  status: 'סטטוס',
};

export const ROW_STATE_LABELS: Readonly<Record<RowState, string>> = {
  stored: 'שמור',
  changed: 'שונה',
  new: 'חדש',
  placeholder: 'שורה חדשה',
};

export const OUTCOME_LABELS: Readonly<Record<UnitBatchRowStatus, string>> = {
  create: 'יחידה חדשה',
  update: 'עדכון',
  unchanged: 'ללא שינוי',
  invalid: 'שגיאה',
  duplicate: 'כפילות',
  conflict: 'התנגשות',
};

export function errorText(field: string, code: string): string {
  const label = (FIELD_LABELS as Readonly<Record<string, string>>)[field] ?? '';
  const message = CELL_ERROR_MESSAGES[code] ?? 'ערך לא תקין';
  return label === '' ? message : `${label}: ${message}`;
}

/**
 * A Hebrew count: the singular phrase for one ("עדכון אחד"), otherwise number + plural
 * ("3 עדכונים"). Avoids forms such as "1 עדכונים".
 */
export function count(n: number, one: string, many: string): string {
  return n === 1 ? one : `${n.toLocaleString('he-IL')} ${many}`;
}

/** Joins two phrases with "ו": a hyphen before a number ("ו-3"), attached before a word ("ועדכון"). */
export function and(first: string, second: string): string {
  return /^\d/.test(second) ? `${first} ו-${second}` : `${first} ו${second}`;
}

/** Column name for messages: the original header, with its secondary label when it repeats. */
export function apartmentColumnLabel(key: string): string {
  const slot = parseSlotKey(key);
  const column =
    slot !== null && slot.slot > 0
      ? extraSlotColumns(slot.kind, slot.slot).find((c) => c.key === key)
      : [...APARTMENT_COLUMNS, ...APARTMENT_SOURCE_COLUMNS].find((c) => c.key === key);
  if (column === undefined) return '';
  return column.secondary === null ? column.header : `${column.header} (${column.secondary})`;
}

export function apartmentErrorText(field: string, code: string): string {
  const label = apartmentColumnLabel(field);
  const message = APARTMENT_ERROR_MESSAGES[code] ?? 'ערך לא תקין';
  return label === '' ? message : `${label}: ${message}`;
}

export const APARTMENT_OUTCOME_LABELS: Readonly<Record<ApartmentRowStatus, string>> = {
  create: 'דירה חדשה',
  update: 'עדכון',
  unchanged: 'ללא שינוי',
  confirm: 'דורש אישור',
  invalid: 'שגיאה',
  duplicate: 'כפילות',
  conflict: 'התנגשות',
};
